/**
 * Global silent-refresh interceptor.
 *
 * The app makes most API calls via raw `fetch(...)` with credentials:'include'
 * and the rest via the AuthContext axios instance. This module patches BOTH so
 * that any 401 on a non-auth endpoint triggers a single POST /api/auth/refresh
 * (with in-flight dedup) and retries the original request transparently.
 *
 * If the refresh itself fails, we flip a global "logged out" flag and broadcast
 * an 'app:session-expired' event so the UI can redirect to /login gracefully.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_PATH = '/api/auth/refresh';

// Endpoints that must NEVER trigger a refresh-retry loop
const AUTH_BYPASS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/logout',
  '/api/auth/refresh',
  '/api/auth/me',
];

const isAuthBypassUrl = (url) => {
  if (!url) return false;
  const u = String(url);
  return AUTH_BYPASS.some(p => u.includes(p));
};

const isOurApi = (url) => {
  if (!url) return false;
  const u = String(url);
  return u.includes('/api/') && (u.startsWith(BACKEND_URL) || u.startsWith('/api'));
};

let inFlightRefresh = null;
let refreshFailedAt = 0;
let sessionExpiredBroadcastAt = 0;

const broadcastSessionExpired = () => {
  // throttle: only broadcast once every 5 seconds to avoid storms
  if (Date.now() - sessionExpiredBroadcastAt < 5000) return;
  sessionExpiredBroadcastAt = Date.now();
  try {
    window.dispatchEvent(new CustomEvent('app:session-expired'));
  } catch (e) { /* old browsers */ }
};

const doRefresh = () => {
  // dedup: a single in-flight refresh shared by all concurrent 401s
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    try {
      const res = await window.__originalFetch__(`${BACKEND_URL}${REFRESH_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('refresh-failed');
      return true;
    } catch (e) {
      refreshFailedAt = Date.now();
      broadcastSessionExpired();
      return false;
    } finally {
      // small delay so we don't immediately re-queue another refresh
      setTimeout(() => { inFlightRefresh = null; }, 200);
    }
  })();
  return inFlightRefresh;
};

export const installAuthRefresh = (axiosInstance) => {
  // ===== fetch interceptor =====
  if (!window.__originalFetch__) {
    window.__originalFetch__ = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const url = typeof input === 'string' ? input : input?.url;
      const res = await window.__originalFetch__(input, init);
      if (res.status === 401 && isOurApi(url) && !isAuthBypassUrl(url)) {
        // avoid retry storms within 5s of a failed refresh
        if (Date.now() - refreshFailedAt < 5000) return res;
        const ok = await doRefresh();
        if (ok) {
          // retry once with same input/init (cookies updated server-side)
          try {
            return await window.__originalFetch__(input, init);
          } catch (e) {
            return res;
          }
        }
      }
      return res;
    };
  }

  // ===== axios interceptor =====
  if (axiosInstance && !axiosInstance.__authRefreshInstalled) {
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        const fullUrl = (axiosInstance.defaults?.baseURL || '') + url;
        if (status === 401 && isOurApi(fullUrl) && !isAuthBypassUrl(fullUrl) && !error.config._retried) {
          if (Date.now() - refreshFailedAt < 5000) return Promise.reject(error);
          const ok = await doRefresh();
          if (ok) {
            error.config._retried = true;
            return axiosInstance.request(error.config);
          }
        }
        return Promise.reject(error);
      }
    );
    axiosInstance.__authRefreshInstalled = true;
  }
};
